export type WarehouseMovementType = 'receipt' | 'issue' | 'transfer' | 'adjustment';
export type WarehouseStockFilter = 'all' | 'low' | 'negative';

export interface WarehouseLocationRecord {
  readonly id: string;
  readonly tenantId: string;
  readonly code: string;
  readonly name: string;
  readonly description: string;
  readonly active: boolean;
  readonly defaultLocation: boolean;
  readonly stockedItemCount: number;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface WarehouseArticleOption {
  readonly id: string;
  readonly number: string;
  readonly name: string;
  readonly unit: string;
  readonly minimumStock: number;
  readonly preferredSupplierId: string | null;
  readonly preferredSupplierName: string;
  readonly preferredSupplierItemNumber: string;
  readonly preferredPurchasePrice: number;
  readonly preferredLeadTimeDays: number | null;
}

export interface WarehouseStockRecord {
  readonly itemId: string;
  readonly itemNumber: string;
  readonly itemName: string;
  readonly unit: string;
  readonly locationId: string;
  readonly locationCode: string;
  readonly locationName: string;
  readonly onHand: number;
  readonly reserved: number;
  readonly available: number;
  readonly minimumStock: number;
  readonly lowStock: boolean;
  readonly negativeStock: boolean;
  readonly preferredSupplierId: string | null;
  readonly preferredSupplierName: string;
  readonly updatedAt: string;
}

export interface WarehouseStockSearchInput {
  readonly tenantId: string;
  readonly query?: string;
  readonly filter?: WarehouseStockFilter;
  readonly locationId?: string | 'all';
  readonly page?: number;
  readonly pageSize?: number;
}

export interface NormalizedWarehouseStockSearchInput {
  readonly tenantId: string;
  readonly query: string;
  readonly filter: WarehouseStockFilter;
  readonly locationId: string | 'all';
  readonly page: number;
  readonly pageSize: number;
}

export interface WarehouseStockPage {
  readonly items: readonly WarehouseStockRecord[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface WarehouseMovementRecord {
  readonly id: string;
  readonly tenantId: string;
  readonly type: WarehouseMovementType;
  readonly itemId: string;
  readonly itemNumber: string;
  readonly itemName: string;
  readonly quantity: number;
  readonly sourceLocationId: string | null;
  readonly sourceLocationCode: string;
  readonly sourceLocationName: string;
  readonly targetLocationId: string | null;
  readonly targetLocationCode: string;
  readonly targetLocationName: string;
  readonly reason: string;
  readonly reference: string;
  readonly actor: string;
  readonly occurredAt: string;
}

export interface WarehouseReplenishmentRecord {
  readonly itemId: string;
  readonly itemNumber: string;
  readonly itemName: string;
  readonly unit: string;
  readonly onHand: number;
  readonly reserved: number;
  readonly available: number;
  readonly minimumStock: number;
  readonly suggestedQuantity: number;
  readonly urgency: 'critical' | 'low';
  readonly supplierId: string | null;
  readonly supplierName: string;
  readonly supplierItemNumber: string;
  readonly purchasePrice: number;
  readonly leadTimeDays: number | null;
}

export interface WarehouseOverview {
  readonly stockedItems: number;
  readonly lowStockItems: number;
  readonly negativeStockItems: number;
  readonly activeLocations: number;
  readonly movementsToday: number;
  readonly openReplenishmentItems: number;
}

export interface SaveWarehouseLocationInput {
  readonly tenantId: string;
  readonly locationId?: string;
  readonly code: string;
  readonly name: string;
  readonly description?: string;
  readonly active?: boolean;
  readonly defaultLocation?: boolean;
}

export interface PostWarehouseMovementInput {
  readonly tenantId: string;
  readonly type: WarehouseMovementType;
  readonly itemId: string;
  readonly quantity: number;
  readonly sourceLocationId?: string | null;
  readonly targetLocationId?: string | null;
  readonly reason: string;
  readonly reference?: string;
}

// --- Inventur (Stichtagsinventur) -------------------------------------------

export type WarehouseCountStatus = 'counting' | 'posted' | 'cancelled';

export interface WarehouseCountLineRecord {
  readonly id: string;
  readonly itemId: string;
  readonly itemNumber: string;
  readonly itemName: string;
  readonly unit: string;
  readonly locationId: string;
  readonly locationCode: string;
  /** Buchbestand-Snapshot bei Session-Start (nur zur Anzeige der Differenz). */
  readonly bookQuantity: number;
  /** Gezählte Menge (null = noch nicht gezählt). */
  readonly countedQuantity: number | null;
  /** countedQuantity − bookQuantity (0, solange nicht gezählt). */
  readonly difference: number;
}

export interface WarehouseCountSessionRecord {
  readonly id: string;
  readonly tenantId: string;
  readonly reference: string;
  readonly status: WarehouseCountStatus;
  /** null = ganzes Lager. */
  readonly locationId: string | null;
  readonly locationCode: string;
  readonly note: string;
  readonly totalLines: number;
  readonly countedLines: number;
  readonly differenceLines: number;
  readonly createdAt: string;
  readonly createdByName: string;
  readonly postedAt: string | null;
  /** Zähllisten-Positionen (nur im Detail gefüllt; in der Liste leer). */
  readonly lines: readonly WarehouseCountLineRecord[];
}

export interface OpenWarehouseCountInput {
  readonly tenantId: string;
  /** null/undefined = ganzes Lager. */
  readonly locationId?: string | null;
  readonly note?: string;
}

export interface SaveWarehouseCountLineInput {
  readonly tenantId: string;
  readonly sessionId: string;
  readonly lineId: string;
  readonly countedQuantity: number | null;
}

export interface PostWarehouseCountInput {
  readonly tenantId: string;
  readonly sessionId: string;
}

export function warehouseCountDifference(bookQuantity: number, countedQuantity: number | null): number {
  return countedQuantity == null ? 0 : Math.round((countedQuantity - bookQuantity) * 1000) / 1000;
}

export interface WarehouseGateway {
  getOverview(tenantId: string): Promise<WarehouseOverview>;
  listLocations(tenantId: string, includeInactive?: boolean): Promise<readonly WarehouseLocationRecord[]>;
  saveLocation(input: SaveWarehouseLocationInput): Promise<WarehouseLocationRecord>;
  searchArticleOptions(tenantId: string, query?: string): Promise<readonly WarehouseArticleOption[]>;
  searchStock(input: WarehouseStockSearchInput): Promise<WarehouseStockPage>;
  listMovements(tenantId: string, limit?: number): Promise<readonly WarehouseMovementRecord[]>;
  postMovement(input: PostWarehouseMovementInput): Promise<WarehouseMovementRecord>;
  listReplenishment(tenantId: string): Promise<readonly WarehouseReplenishmentRecord[]>;
  /** Inventur: Zählsession öffnen -- schnappt den Buchbestand aller bestandsgeführten Artikel als Zählliste. */
  openCountSession(input: OpenWarehouseCountInput): Promise<WarehouseCountSessionRecord>;
  listCountSessions(tenantId: string): Promise<readonly WarehouseCountSessionRecord[]>;
  getCountSession(tenantId: string, sessionId: string): Promise<WarehouseCountSessionRecord>;
  saveCountLine(input: SaveWarehouseCountLineInput): Promise<WarehouseCountLineRecord>;
  /** Abschluss: bucht jede Differenz (gezählt − aktueller Bestand) als Korrektur über den Bewegungslayer. */
  postCountSession(input: PostWarehouseCountInput): Promise<WarehouseCountSessionRecord>;
}

export function normalizeWarehouseStockSearchInput(
  input: WarehouseStockSearchInput,
): NormalizedWarehouseStockSearchInput {
  return {
    tenantId: input.tenantId,
    query: input.query?.trim() ?? '',
    filter: input.filter ?? 'all',
    locationId: input.locationId ?? 'all',
    page: Math.max(1, Math.trunc(input.page ?? 1)),
    pageSize: Math.min(100, Math.max(1, Math.trunc(input.pageSize ?? 25))),
  };
}

export function normalizeWarehouseLocationInput(
  input: SaveWarehouseLocationInput,
): SaveWarehouseLocationInput {
  const code = input.code.trim().toUpperCase();
  const name = input.name.trim();
  if (!/^[A-Z0-9][A-Z0-9_-]{0,19}$/.test(code)) {
    throw new Error('Der Lagerplatz-Code darf nur Großbuchstaben, Zahlen, Bindestriche und Unterstriche enthalten.');
  }
  if (!name) throw new Error('Der Lagerplatz benötigt eine Bezeichnung.');
  return {
    tenantId: input.tenantId,
    ...(input.locationId ? { locationId: input.locationId } : {}),
    code,
    name,
    description: input.description?.trim() ?? '',
    active: input.active ?? true,
    defaultLocation: input.defaultLocation ?? false,
  };
}

export function normalizeWarehouseMovementInput(
  input: PostWarehouseMovementInput,
): PostWarehouseMovementInput {
  const quantity = Number(input.quantity);
  const reason = input.reason.trim();
  if (!input.itemId) throw new Error('Für die Buchung muss ein Artikel ausgewählt werden.');
  if (!Number.isFinite(quantity) || quantity === 0) throw new Error('Die Menge muss ungleich null sein.');
  if (reason.length < 3) throw new Error('Bitte einen nachvollziehbaren Buchungsgrund angeben.');

  const sourceLocationId = input.sourceLocationId || null;
  const targetLocationId = input.targetLocationId || null;

  if (input.type === 'receipt') {
    if (quantity <= 0 || !targetLocationId || sourceLocationId) {
      throw new Error('Ein Zugang benötigt eine positive Menge und einen Ziellagerplatz.');
    }
  } else if (input.type === 'issue') {
    if (quantity <= 0 || !sourceLocationId || targetLocationId) {
      throw new Error('Ein Abgang benötigt eine positive Menge und einen Quelllagerplatz.');
    }
  } else if (input.type === 'transfer') {
    if (quantity <= 0 || !sourceLocationId || !targetLocationId || sourceLocationId === targetLocationId) {
      throw new Error('Eine Umlagerung benötigt zwei unterschiedliche Lagerplätze und eine positive Menge.');
    }
  } else if (!targetLocationId || sourceLocationId) {
    throw new Error('Eine Korrektur benötigt einen Lagerplatz.');
  }

  return {
    tenantId: input.tenantId,
    type: input.type,
    itemId: input.itemId,
    quantity: Math.round(quantity * 1000) / 1000,
    sourceLocationId,
    targetLocationId,
    reason,
    reference: input.reference?.trim() ?? '',
  };
}
